import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import ErrorMessage from './ErrorMessage'

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4444/v1/public'

// Toggle between live tag stats/definitions and the stored snapshot.
// While the live tag definitions are being updated, the public view reads from
// a snapshot so the figures stay aligned with the legacy tag names.
// Flip USE_SNAPSHOT back to false to return to the live endpoints.
const USE_SNAPSHOT = true
// Snapshot to read: the most recent snapshot taken on or before this date.
const SNAPSHOT_DATE = '2026-06-18'

const CATEGORIES_PATH = USE_SNAPSHOT
  ? '/opportunities/categories-snapshot'
  : '/opportunities/categories'
const TAG_STATS_PATH = USE_SNAPSHOT
  ? '/opportunities/tag-stats-snapshot'
  : '/opportunities/tag-stats'

interface StatItem {
  category: string
  count: number
}

interface OpportunityTag {
  value: string
  name: string
  description: string
}

interface Region {
  id: number
  name: string
  maori_name: string
}

interface OppTagData {
  data: {
    chart_type: string
    stats: StatItem[]
    total_opportunities: number
  }
}

// Color palette for categories
const CATEGORY_COLORS = [
  '#ef3636ff', // Red
  '#69fff5ff', // Teal
  '#45B7D1', // Sky Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Light Blue
  '#F8B739', // Orange
  '#52BE80', // Green
  '#EC7063', // Coral
  '#5DADE2', // Blue
  '#F5B7B1', // Pink
  '#FAD7A0', // Peach
  '#D7BDE2', // Lavender
  '#A9DFBF', // Light Green
  '#F9E79F', // Pale Yellow
  '#888c8fff', // Powder Blue
  '#E59866', // Tan
  '#A569BD', // Deep Purple
]

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          border: '2px solid hsl(210.73, 42.27%, 19.02%)',
          borderRadius: 0,
          p: 1.5,
        }}
      >
        <Typography
          sx={{ color: 'hsl(210.73, 42.27%, 19.02%)', fontWeight: 600 }}
        >
          {data.category}
        </Typography>
        <Typography sx={{ color: 'hsl(210.73, 42.27%, 19.02%)' }}>
          {data.percentage}%
        </Typography>
      </Box>
    )
  }
  return null
}

export default function OppTagStats() {
  // Responsive breakpoint - only shrink on true mobile, not tablets
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Filter state - only region selection
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null)

  // Data state
  const [stats, setStats] = useState<StatItem[]>([])
  const [totalOpportunities, setTotalOpportunities] = useState<number>(0)

  // Category list expansion state
  const [isExpanded, setIsExpanded] = useState(false)

  // Scroll animation state
  const [isChartVisible, setIsChartVisible] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartKey, setChartKey] = useState(0)

  // Intersection Observer for scroll-triggered animation
  useEffect(() => {
    const currentRef = chartRef.current
    if (!currentRef) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isChartVisible) {
            setIsChartVisible(true)
          }
        })
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      },
    )

    observer.observe(currentRef)

    // Check if element is already visible on mount (handles page refresh)
    const rect = currentRef.getBoundingClientRect()
    const isAlreadyVisible = rect.top < window.innerHeight && rect.bottom > 0
    if (isAlreadyVisible && !isChartVisible) {
      setIsChartVisible(true)
    }

    return () => {
      observer.unobserve(currentRef)
    }
  }, [isChartVisible, stats.length])

  // Trigger animation when chart becomes visible
  useEffect(() => {
    if (isChartVisible) {
      setChartKey((prev) => prev + 1)
    }
  }, [isChartVisible])

  // Don't re-trigger animation on filter change - let Recharts handle the data transition smoothly

  // Fetch categories (tags)
  const { data: categories, isLoading: categoriesIsLoading } = useQuery({
    queryKey: ['opportunity_categories'],
    queryFn: async () => {
      const response = await axios.get(`${apiUrl}${CATEGORIES_PATH}`)
      return { data: response.data, total: response.data.length }
    },
  })

  const availableTags = useMemo(() => {
    const categoriesList = (categories?.data as OpportunityTag[]) || []
    return categoriesList
      .map((cat) => cat.value)
      .filter((tag) => tag && tag.trim() !== '')
      .sort()
  }, [categories?.data])

  // Create category color map based on alphabetically sorted tags
  const categoryColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableTags.forEach((tag, index) => {
      map[tag] = CATEGORY_COLORS[index % CATEGORY_COLORS.length]
    })
    return map
  }, [availableTags])

  // Fetch regions
  const { data: regionOptions, isLoading: regionsIsLoading } = useQuery({
    queryKey: ['regions'],
    queryFn: async () => {
      const response = await axios.get(`${apiUrl}/regions/`, {
        params: { paginate: false },
      })
      return {
        data: response.data,
        total: Number(response.headers['x-total-count']),
      }
    },
  })

  const availableBaseRegions = useMemo(() => {
    const regions =
      (regionOptions?.data as Region[])?.filter(
        (r) => r.name && r.name.trim() !== '',
      ) || []

    // Sort with New Zealand at the top, then alphabetically
    return regions.sort((a, b) => {
      if (a.name === 'New Zealand') return -1
      if (b.name === 'New Zealand') return 1
      return a.name.localeCompare(b.name)
    })
  }, [regionOptions?.data])

  // Build query params - only region (but not for "New Zealand" which is national view)
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (selectedRegion && selectedRegion.name !== 'New Zealand') {
      params.append('region', selectedRegion.id.toString())
    }
    if (USE_SNAPSHOT) {
      params.append('snapshot_date', SNAPSHOT_DATE)
    }
    return params.toString()
  }, [selectedRegion])

  // API call for tag stats with data transformation
  const {
    isLoading,
    isFetching,
    isError,
    data: tagStatsData,
  } = useQuery<OppTagData>({
    queryKey: ['oppTagStats', queryParams],
    queryFn: async () => {
      const baseUrl = `${apiUrl}${TAG_STATS_PATH}`
      const url = queryParams
        ? `${baseUrl}${queryParams.startsWith('?') ? '' : '?'}${queryParams}`
        : baseUrl
      const response = await axios.get(url)
      return response.data
    },
    enabled: true,
    placeholderData: (previousData) => previousData, // Keep showing old data while fetching new
    staleTime: 0, // Always refetch on filter change
  })

  // Always fetch national stats for comparison
  const { data: nationalStatsData } = useQuery<OppTagData>({
    queryKey: ['nationalTagStats', USE_SNAPSHOT ? SNAPSHOT_DATE : 'live'],
    queryFn: async () => {
      const baseUrl = `${apiUrl}${TAG_STATS_PATH}`
      const url = USE_SNAPSHOT
        ? `${baseUrl}?snapshot_date=${encodeURIComponent(SNAPSHOT_DATE)}`
        : baseUrl
      const response = await axios.get(url)
      return response.data
    },
    enabled: true,
    staleTime: 300000, // Cache for 5 minutes since national stats don't change often
  })

  // Process tag stats data when it changes - memoized to prevent unnecessary recalculations
  const processedStats = useMemo(() => {
    if (!tagStatsData || isLoading || isError) return { stats: [], total: 0 }

    const data = tagStatsData.data || tagStatsData
    let statsData = data.stats || []

    // Fill in 0 counts for missing tags
    const statsMap = new Map(statsData.map((s: StatItem) => [s.category, s]))
    const allStatsWithZeros = [...statsData]
    availableTags.forEach((tag) => {
      if (!statsMap.has(tag)) {
        allStatsWithZeros.push({ category: tag, count: 0 })
      }
    })
    statsData = allStatsWithZeros

    // Sort alphabetically by category
    statsData.sort((a: StatItem, b: StatItem) =>
      a.category.localeCompare(b.category),
    )

    return { stats: statsData, total: data.total_opportunities || 0 }
  }, [tagStatsData, availableTags, isLoading, isError])

  // Update state only when processed data changes
  useEffect(() => {
    setStats(processedStats.stats)
    setTotalOpportunities(processedStats.total)
  }, [processedStats])

  // Calculate national percentages for comparison
  const nationalPercentages = useMemo(() => {
    if (!nationalStatsData) return new Map<string, number>()

    const data = nationalStatsData.data || nationalStatsData
    const nationalStats = data.stats || []
    const nationalTotal = data.total_opportunities || 0

    const percentages = new Map<string, number>()
    nationalStats.forEach((stat: StatItem) => {
      const percentage =
        nationalTotal > 0 ? (stat.count / nationalTotal) * 100 : 0
      percentages.set(stat.category, percentage)
    })

    return percentages
  }, [nationalStatsData])

  // Prepare pie chart data
  const pieChartData = useMemo(
    () =>
      stats
        .filter((stat) => stat.count > 0) // Exclude zero-count items from chart
        .map((stat) => {
          const percentage =
            totalOpportunities > 0
              ? ((stat.count / totalOpportunities) * 100).toFixed(1)
              : '0.0'
          return {
            name: `${stat.category} (${stat.count})`,
            value: stat.count,
            category: stat.category,
            percentage: percentage,
          }
        })
        .sort((a, b) => b.value - a.value), // Sort by percentage (count) descending
    [stats, totalOpportunities],
  )

  return (
    <Box sx={{ minHeight: '100vh', mb: 4 }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          p: 4,
          minHeight: '100vh',
        }}
      >
        {/* Show loading spinner only on initial load (when no data exists yet) */}
        {(isLoading || categoriesIsLoading || regionsIsLoading) &&
          !stats.length && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          )}

        {isError && <ErrorMessage />}

        {/* Always show chart if we have data (even during refetch) */}
        {(stats.length > 0 ||
          (!isLoading && !categoriesIsLoading && !regionsIsLoading)) && (
          <>
            {/* Pie Chart and Category List Side by Side */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: 'center',
              }}
            >
              {/* Pie Chart */}
              {pieChartData.length > 0 &&
              pieChartData.length <= availableTags.length ? (
                <Box
                  ref={chartRef}
                  sx={{
                    flex: '0 0 auto',
                    width: { xs: '100%', md: '500px' },
                    height: 400,
                    position: 'relative',
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        key={chartKey}
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={isMobile ? 100 : 200}
                        fill="#8884d8"
                        dataKey="value"
                        isAnimationActive={true}
                        animationBegin={0}
                        animationDuration={800}
                      >
                        {pieChartData.map((entry, index) => {
                          const color =
                            categoryColorMap[entry.category] ||
                            CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                          return <Cell key={`cell-${index}`} fill={color} />
                        })}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Logo positioned at bottom left */}
                  <Box
                    component="img"
                    src="https://app.fundsorter.com/public/Fundsorter-h-black800px.png"
                    alt="Fundsorter Logo"
                    sx={{
                      position: 'absolute',
                      bottom: -55,
                      left: 20,
                      height: 45,
                      opacity: 0.7,
                    }}
                  />
                </Box>
              ) : pieChartData.length > availableTags.length ? (
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: { xs: '100%', md: '500px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 400,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    Chart hidden - too many segments ({pieChartData.length}).
                    See list.
                  </Typography>
                </Box>
              ) : null}

              {/* Region Filter and Scrollable Category List */}
              <Box sx={{ flex: 1, width: '100%' }}>
                {/* Compact Region Filter */}
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 6,
                  }}
                >
                  <Autocomplete
                    options={availableBaseRegions}
                    value={
                      selectedRegion ||
                      availableBaseRegions.find(
                        (r: Region) => r.name === 'New Zealand',
                      )
                    }
                    defaultValue={availableBaseRegions.find(
                      (r: Region) => r.name === 'New Zealand',
                    )}
                    onChange={(_, newValue) => {
                      setSelectedRegion(newValue)
                    }}
                    getOptionLabel={(option) => {
                      if (!option.name) return ''
                      return option.maori_name
                        ? `${option.name} - ${option.maori_name}`
                        : option.name
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.maori_name
                          ? `${option.name} - ${option.maori_name}`
                          : option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by region"
                        placeholder="Select a region..."
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            '& fieldset': {
                              borderColor: 'hsl(210.73, 42.27%, 19.02%)',
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: 'hsl(210.73, 42.27%, 19.02%)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'hsl(210.73, 42.27%, 19.02%)',
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'hsl(210.73, 42.27%, 19.02%)',
                            '&.Mui-focused': {
                              color: 'hsl(210.73, 42.27%, 19.02%)',
                            },
                          },
                          '& .MuiAutocomplete-popupIndicator': {
                            color: 'hsl(210.73, 42.27%, 19.02%)',
                          },
                          '& .MuiAutocomplete-clearIndicator': {
                            color: 'hsl(210.73, 42.27%, 19.02%)',
                          },
                        }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  {isFetching && (
                    <CircularProgress
                      size={24}
                      sx={{ color: 'hsl(210.73, 42.27%, 19.02%)' }}
                    />
                  )}
                </Box>

                {/* Scrollable Category List */}
                <Box
                  sx={{
                    width: '100%',
                    border: '2px solid hsl(210.73, 42.27%, 19.02%)',
                    borderRadius: 0,
                    maxHeight: 400,
                    overflowY: 'auto',
                    minWidth: 0,
                    backgroundColor: 'transparent',
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                      width: '12px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'hsl(163, 20%, 92%)',
                      borderRadius: 0,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'hsl(210.73, 42.27%, 19.02%)',
                      borderRadius: 0,
                      border: '2px solid hsl(163, 20%, 92%)',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: 'hsl(210.73, 42.27%, 25%)',
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: 'auto',
                    scrollbarColor:
                      'hsl(210.73, 42.27%, 19.02%) hsl(163, 20%, 92%)',
                  }}
                >
                  <List>
                    {stats
                      .filter((stat) => stat.count > 0)
                      .sort((a, b) => b.count - a.count)
                      .map((stat, index) => {
                        const color =
                          categoryColorMap[stat.category] ||
                          CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                        const percentage =
                          totalOpportunities > 0
                            ? ((stat.count / totalOpportunities) * 100).toFixed(
                                1,
                              )
                            : '0.0'

                        // Calculate difference from national if a sub-region is selected
                        // Don't show comparison for "New Zealand" itself
                        const nationalPct = nationalPercentages.get(
                          stat.category,
                        )
                        const isSubRegionSelected =
                          selectedRegion !== null &&
                          selectedRegion.name !== 'New Zealand'
                        const diff =
                          nationalPct !== undefined && isSubRegionSelected
                            ? parseFloat(percentage) - nationalPct
                            : null

                        return (
                          <ListItem
                            key={stat.category}
                            sx={{
                              borderBottom:
                                index <
                                stats.filter((s) => s.count > 0).length - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none',
                              py: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: color,
                                mr: 2,
                                borderRadius: 0,
                                flexShrink: 0,
                              }}
                            />
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 600,
                                      color: 'hsl(210.73, 42.27%, 19.02%)',
                                    }}
                                  >
                                    {stat.category} ({percentage}%)
                                  </Typography>
                                  {diff !== null && Math.abs(diff) > 0.1 && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        color:
                                          diff > 0
                                            ? '#2e7d32'
                                            : diff < 0
                                              ? '#d32f2f'
                                              : 'gray',
                                        backgroundColor:
                                          diff > 0
                                            ? '#e8f5e9'
                                            : diff < 0
                                              ? '#ffebee'
                                              : '#f5f5f5',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '4px',
                                      }}
                                    >
                                      {diff > 0 ? '+' : ''}
                                      {diff.toFixed(1)}%
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        )
                      })}
                  </List>
                </Box>
                {/* Explanation note for regional comparison */}
                {selectedRegion !== null &&
                  selectedRegion.name !== 'New Zealand' && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        color: 'hsl(210.73, 42.27%, 40%)',
                        fontStyle: 'italic',
                        fontSize: '0.75rem',
                      }}
                    >
                      Colored badges show the difference compared to national
                      statistics
                    </Typography>
                  )}
              </Box>
            </Box>

            {/* Expandable Category List */}
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: isExpanded ? 'auto' : '150px',
                  overflow: 'hidden',
                  border: '2px solid hsl(210.73, 42.27%, 19.02%)',
                  borderRadius: 0,
                }}
              >
                {/* Gradient mask overlay */}
                {!isExpanded && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '40px',
                      background:
                        'linear-gradient(transparent, hsl(163, 20%, 85%))',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                  />
                )}

                {/* Category table */}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: 'hsl(210.73, 42.27%, 19.02%)',
                          borderBottom: '2px solid hsl(210.73, 42.27%, 19.02%)',
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: 'hsl(210.73, 42.27%, 19.02%)',
                          borderBottom: '2px solid hsl(210.73, 42.27%, 19.02%)',
                        }}
                      >
                        %
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: 'hsl(210.73, 42.27%, 19.02%)',
                          borderBottom: '2px solid hsl(210.73, 42.27%, 19.02%)',
                        }}
                      >
                        Description
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(categories?.data as OpportunityTag[])
                      ?.sort((a, b) => a.value.localeCompare(b.value))
                      .map((category, index) => {
                        // Find the stat for this category
                        const stat = stats.find(
                          (s) => s.category === category.value,
                        )
                        const percentage =
                          stat && totalOpportunities > 0
                            ? ((stat.count / totalOpportunities) * 100).toFixed(
                                1,
                              )
                            : '0.0'

                        return (
                          <TableRow key={category.value}>
                            <TableCell
                              sx={{
                                color: 'hsl(210.73, 42.27%, 19.02%)',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            >
                              {category.name}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: 'hsl(210.73, 42.27%, 19.02%)',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            >
                              {percentage}%
                            </TableCell>
                            <TableCell
                              sx={{
                                color: 'hsl(210.73, 42.27%, 19.02%)',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            >
                              {category.description}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </Box>

              {/* Expand/Collapse button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{
                    color: 'hsl(210.73, 42.27%, 19.02%)',
                    border: '2px solid hsl(210.73, 42.27%, 19.02%)',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {isExpanded ? (
                    <>
                      <KeyboardArrowUpIcon /> Show Less
                    </>
                  ) : (
                    <>
                      <KeyboardArrowDownIcon /> Show More
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
